import { useState, useEffect, useRef } from "react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { CheckCircle2, PenTool, X, Loader2, FileText, Phone, Mail, Square, CheckSquare, CreditCard, Wallet, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { useParams } from "react-router-dom";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PublicQuotePage() {
  const { token } = useParams();
  const [data, setData]           = useState(null);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);
  const [step, setStep]           = useState("view"); // view | select | sign | done
  const [selectedItems, setSelectedItems] = useState({}); // {index: true/false}
  const [clientForm, setClientForm] = useState({ client_name: "", client_email: "", client_phone: "" });
  const [saving, setSaving]       = useState(false);
  const [hasSig, setHasSig]       = useState(false);
  const [isDrawing, setIsDrawing] = useState(false);
  const [etatPaiement, setEtatPaiement] = useState(null);   // montants renvoyés par l'API
  const [paiementEnCours, setPaiementEnCours] = useState(null); // "total" | "acompte"
  const canvasRef = useRef(null);

  useEffect(() => { fetchQuote(); }, [token]);

  // État de paiement (montants, acompte, solde) — rechargé au retour de PayPlug
  const chargerPaiement = async () => {
    try {
      const r = await axios.get(`${API_URL}/api/public/quotes/${token}/payment-status`);
      setEtatPaiement(r.data);
      return r.data;
    } catch { return null; }
  };

  useEffect(() => {
    chargerPaiement().then((e) => {
      const retour = new URLSearchParams(window.location.search).get("payment");
      if (!retour) return;
      window.history.replaceState({}, "", window.location.pathname);
      if (retour === "success") {
        toast.success("Paiement enregistré, merci !");
        setStep("pay");
        // le webhook PayPlug peut arriver avec un léger décalage
        setTimeout(chargerPaiement, 2500);
      } else if (retour === "cancelled") {
        toast.info("Paiement annulé — vous pouvez réessayer.");
        if (e?.solde_du) setStep("pay");
      }
    });
  }, [token]); // eslint-disable-line

  const lancerPaiement = async (mode) => {
    setPaiementEnCours(mode);
    try {
      const r = await axios.post(`${API_URL}/api/public/quotes/${token}/create-payment`, { mode });
      window.location.href = r.data.payment_url;   // page de paiement sécurisée PayPlug
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible d'ouvrir le paiement");
      setPaiementEnCours(null);
    }
  };

  const fetchQuote = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/public/quotes/${token}`);
      setData(res.data);
      document.title = `Devis ${res.data.quote?.quote_number || ""} — ${res.data.shop?.shop_name || "AMCPhone"}`;
      setClientForm({
        client_name:  res.data.quote.client_name  || "",
        client_email: res.data.quote.client_email || "",
        client_phone: res.data.quote.client_phone || "",
      });
      // Tous les articles sélectionnés par défaut
      const defaults = {};
      (res.data.quote.items || []).forEach((_, i) => { defaults[i] = true; });
      setSelectedItems(defaults);
    } catch {
      setError("Ce lien est invalide ou a expiré.");
    } finally {
      setLoading(false);
    }
  };

  const toggleItem = (i) => {
    setSelectedItems(prev => ({ ...prev, [i]: !prev[i] }));
  };

  const getSelectedItems = () => {
    const { quote } = data;
    return (quote.items || []).filter((_, i) => selectedItems[i]);
  };

  // Remise par unité sur chaque ligne (même calcul que le backend : routes/quote_lines.py)
  const remiseU = (i) => Math.min(Math.max(parseFloat(i.discount) || 0, 0), Math.max(parseFloat(i.unit_price) || 0, 0));
  const totalLigne = (i) => (parseFloat(i.unit_price || 0) - remiseU(i)) * parseFloat(i.quantity || 1);
  const calcTotal = (items, tva) => {
    const sub = (items || []).reduce((s, i) => s + totalLigne(i), 0);
    const remises = (items || []).reduce((s, i) => s + remiseU(i) * parseFloat(i.quantity || 1), 0);
    const tvaAmt = sub * (parseFloat(tva) || 0) / 100;
    return { subtotal: sub, remises, tvaAmt, total: sub + tvaAmt };
  };

  // Canvas signature
  const startDraw = (e) => {
    setIsDrawing(true);
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    ctx.strokeStyle = "#1E293B"; ctx.lineWidth = 2; ctx.lineCap = "round";
    const rect = canvas.getBoundingClientRect();
    const x = ((e.touches?.[0]?.clientX || e.clientX) - rect.left) * (canvas.width / rect.width);
    const y = ((e.touches?.[0]?.clientY || e.clientY) - rect.top) * (canvas.height / rect.height);
    ctx.beginPath(); ctx.moveTo(x, y);
  };
  const draw = (e) => {
    if (!isDrawing) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    const rect = canvas.getBoundingClientRect();
    const x = ((e.touches?.[0]?.clientX || e.clientX) - rect.left) * (canvas.width / rect.width);
    const y = ((e.touches?.[0]?.clientY || e.clientY) - rect.top) * (canvas.height / rect.height);
    ctx.lineTo(x, y); ctx.stroke(); setHasSig(true);
  };
  const stopDraw = () => setIsDrawing(false);
  const clearSig = () => {
    canvasRef.current.getContext("2d").clearRect(0, 0, 600, 160);
    setHasSig(false);
  };

  const handleSign = async () => {
    if (!clientForm.client_name.trim()) { toast.error("Votre nom est requis"); return; }
    if (!hasSig) { toast.error("Veuillez signer dans le cadre"); return; }
    const selected = getSelectedItems();
    if (selected.length === 0) { toast.error("Sélectionnez au moins un article"); return; }
    setSaving(true);
    try {
      const sig = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API_URL}/api/public/quotes/${token}/sign`, {
        ...clientForm,
        signature: sig,
        selected_items: selected,
        // le serveur reprend les lignes enregistrées : on indique seulement lesquelles sont cochées
        selected_indexes: (data.quote.items || []).map((_, i) => i).filter((i) => selectedItems[i]),
      });
      toast.success("Devis signé avec succès !");
      const e = await chargerPaiement();
      // S'il reste quelque chose à régler et que le paiement en ligne est actif,
      // on enchaîne directement sur l'écran de règlement.
      setStep(e?.payplug_enabled && e?.solde_du ? "pay" : "done");
    } catch {
      toast.error("Erreur lors de la signature");
    } finally { setSaving(false); }
  };

  if (loading) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
    </div>
  );

  if (error) return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="text-center">
        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <h2 className="text-xl font-bold text-gray-700 mb-2">Lien invalide</h2>
        <p className="text-gray-500">{error}</p>
      </div>
    </div>
  );

  if (step === "pay") {
    const e = etatPaiement || {};
    const eur = (v) => `${(Number(v) || 0).toFixed(2).replace(".", ",")} €`;
    const acompteVerse = (e.paye || 0) > 0;
    const solde = !e.solde_du;

    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <CheckCircle2 className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-bold text-gray-800">Devis accepté</h2>
            <p className="text-gray-500 text-sm mt-1">
              {solde ? "Votre règlement est complet. Merci !" : "Il ne reste plus qu'à régler votre devis."}
            </p>
          </div>

          <Card className="mb-4 bg-white border-gray-200 shadow-sm">
            <CardContent className="p-5 space-y-2 text-sm">
              <div className="flex justify-between text-gray-600">
                <span>Total du devis</span><span className="font-semibold text-gray-800">{eur(e.total)}</span>
              </div>
              {acompteVerse && (
                <div className="flex justify-between text-green-700">
                  <span>Déjà réglé</span><span className="font-semibold">− {eur(e.paye)}</span>
                </div>
              )}
              <div className="flex justify-between pt-2 border-t text-base">
                <span className="font-semibold text-gray-800">Reste à payer</span>
                <span className="font-bold text-blue-600">{eur(e.solde)}</span>
              </div>
            </CardContent>
          </Card>

          {solde ? (
            <div className="p-4 bg-green-50 rounded-xl text-center text-green-800 text-sm font-medium">
              ✅ Ce devis est intégralement réglé.
            </div>
          ) : (
            <div className="space-y-3">
              <Button
                onClick={() => lancerPaiement("total")}
                disabled={!!paiementEnCours}
                className="w-full h-auto py-4 flex items-center justify-between bg-blue-600 hover:bg-blue-700"
              >
                <span className="flex items-center gap-2">
                  {paiementEnCours === "total"
                    ? <Loader2 className="w-5 h-5 animate-spin" />
                    : <CreditCard className="w-5 h-5" />}
                  <span className="font-semibold">{acompteVerse ? "Régler le solde" : "Payer la totalité"}</span>
                </span>
                <span className="font-bold">{eur(e.solde)}</span>
              </Button>

              {!acompteVerse && e.acompte_possible && (
                <Button
                  onClick={() => lancerPaiement("acompte")}
                  disabled={!!paiementEnCours}
                  variant="outline"
                  className="w-full h-auto py-4 flex items-center justify-between border-2 border-gray-300 bg-white text-gray-800 hover:bg-gray-50 hover:text-gray-900"
                >
                  <span className="flex items-center gap-2">
                    {paiementEnCours === "acompte"
                      ? <Loader2 className="w-5 h-5 animate-spin" />
                      : <Wallet className="w-5 h-5" />}
                    <span className="font-semibold">
                      Verser l'acompte ({e.acompte_pct || 30} %)
                    </span>
                  </span>
                  <span className="font-bold">{eur(e.acompte)}</span>
                </Button>
              )}

              <p className="text-xs text-gray-500 text-center pt-1 flex items-center justify-center gap-1.5">
                <ShieldCheck className="w-3.5 h-3.5" />
                Paiement sécurisé par carte bancaire. Le solde reste dû à la livraison.
              </p>
            </div>
          )}

          <button
            onClick={() => setStep("view")}
            className="mt-4 w-full text-sm text-gray-500 hover:text-gray-700 underline"
          >
            Revoir le détail du devis
          </button>

          <div className="mt-6 p-4 bg-blue-50 rounded-xl text-sm text-blue-700 text-center">
            <p><strong>{data?.shop?.shop_name}</strong></p>
            {data?.shop?.shop_phone && <p className="flex items-center justify-center gap-1 mt-1"><Phone className="w-3.5 h-3.5" />{data.shop.shop_phone}</p>}
            {data?.shop?.shop_email && <p className="flex items-center justify-center gap-1 mt-1"><Mail className="w-3.5 h-3.5" />{data.shop.shop_email}</p>}
          </div>
        </div>
      </div>
    );
  }

  if (step === "done") return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="text-center max-w-sm">
        <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <CheckCircle2 className="w-10 h-10 text-green-600" />
        </div>
        <h2 className="text-2xl font-bold text-gray-800 mb-2">Devis accepté !</h2>
        <p className="text-gray-500 mb-4">Votre signature a bien été enregistrée. Nous allons prendre contact avec vous prochainement.</p>
        <div className="p-4 bg-blue-50 rounded-xl text-sm text-blue-700">
          <p><strong>{data?.shop?.shop_name}</strong></p>
          {data?.shop?.shop_phone && <p className="flex items-center justify-center gap-1 mt-1"><Phone className="w-3.5 h-3.5" />{data.shop.shop_phone}</p>}
          {data?.shop?.shop_email && <p className="flex items-center justify-center gap-1 mt-1"><Mail className="w-3.5 h-3.5" />{data.shop.shop_email}</p>}
        </div>
      </div>
    </div>
  );

  const { quote, shop } = data;
  const alreadySigned = quote.status === "accepte" && quote.client_signature;
  const selectedList = getSelectedItems();
  const { subtotal: selSub, remises: selRemises, tvaAmt: selTva, total: selTotal } = calcTotal(selectedList, quote.tva_rate);
  const { subtotal, remises, tvaAmt, total } = calcTotal(quote.items, quote.tva_rate);
  const remisesAffichees = !alreadySigned ? selRemises : remises;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 py-4">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div>
            <h1 className="font-bold text-lg text-gray-800">{shop?.shop_name}</h1>
            <p className="text-sm text-gray-500">{shop?.shop_address}</p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Devis</p>
            <p className="font-bold text-blue-600">{quote.quote_number}</p>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto p-4 space-y-4">

        {/* Déjà signé */}
        {alreadySigned && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-3">
            <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />
            <div>
              <p className="font-medium text-green-800">Devis signé et accepté</p>
              <p className="text-sm text-green-600">Signé le {new Date(quote.signed_at).toLocaleDateString("fr-FR")}</p>
            </div>
          </div>
        )}

        {/* Règlement encore dû : accessible à tout moment, y compris si le client
            a quitté la page après la signature et revient plus tard par son lien. */}
        {alreadySigned && etatPaiement?.payplug_enabled && etatPaiement?.solde_du && (
          <Card className="bg-white border-blue-200 shadow-sm">
            <CardContent className="p-5">
              <div className="flex items-center justify-between gap-3 mb-3">
                <div>
                  <p className="font-semibold text-gray-800">Reste à régler</p>
                  <p className="text-sm text-gray-500">
                    {(etatPaiement.paye || 0) > 0 ? "Acompte déjà versé" : "Paiement en ligne sécurisé"}
                  </p>
                </div>
                <p className="text-2xl font-bold text-blue-600">
                  {`${(Number(etatPaiement.solde) || 0).toFixed(2).replace(".", ",")} €`}
                </p>
              </div>
              <Button onClick={() => setStep("pay")} className="w-full bg-blue-600 hover:bg-blue-700">
                <CreditCard className="w-4 h-4 mr-2" /> Régler maintenant
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Devis signé et intégralement réglé : on le dit, sinon le client
            revient sur la page sans savoir où en est son paiement. */}
        {alreadySigned && etatPaiement && !etatPaiement.solde_du && (etatPaiement.paye || 0) > 0 && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-center text-green-800 text-sm font-medium">
            ✅ Ce devis est intégralement réglé. Merci !
          </div>
        )}

        {/* Infos devis + sélection articles */}
        <Card className="bg-white border-gray-200 shadow-sm">
          <CardContent className="p-5">
            <div className="flex justify-between items-start mb-4">
              <div>
                <p className="text-xs text-gray-400 uppercase font-medium mb-1">Objet</p>
                <p className="font-semibold text-gray-800 text-lg">{quote.subject || "Devis"}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-400">Date</p>
                <p className="text-sm text-gray-600">{new Date(quote.created_at).toLocaleDateString("fr-FR")}</p>
                {quote.validity_date && (
                  <>
                    <p className="text-xs text-gray-400 mt-1">Valable jusqu'au</p>
                    <p className="text-sm font-medium text-blue-600">{new Date(quote.validity_date).toLocaleDateString("fr-FR")}</p>
                  </>
                )}
              </div>
            </div>

            {/* Message sélection */}
            {!alreadySigned && step !== "sign" && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-3">
                <p className="text-xs text-blue-700 font-medium">
                  ✅ Cochez les articles que vous souhaitez accepter puis cliquez sur "Procéder à la signature"
                </p>
              </div>
            )}

            {/* Tableau articles avec checkboxes */}
            <div className="space-y-1">
              <div className="grid grid-cols-12 gap-2 text-xs text-gray-400 font-medium uppercase pb-1 border-b border-gray-100">
                {!alreadySigned && <span className="col-span-1"></span>}
                <span className={!alreadySigned ? "col-span-4" : "col-span-5"}>Description</span>
                <span className="col-span-1 text-center">Qté</span>
                <span className="col-span-2 text-right">Prix HT</span>
                <span className="col-span-2 text-right">Remise</span>
                <span className="col-span-2 text-right">Total</span>
              </div>
              {(quote.items || []).map((item, i) => {
                const isSelected = selectedItems[i] !== false;
                const lineTotal = totalLigne(item);
                const remise = remiseU(item);
                return (
                  <div
                    key={i}
                    className={`grid grid-cols-12 gap-2 text-sm py-2 border-b border-gray-50 transition-all ${!alreadySigned ? "cursor-pointer hover:bg-gray-50" : ""} ${!isSelected && !alreadySigned ? "opacity-40" : ""}`}
                    onClick={() => !alreadySigned && toggleItem(i)}
                  >
                    {!alreadySigned && (
                      <span className="col-span-1 flex items-center">
                        {isSelected
                          ? <CheckSquare className="w-4 h-4 text-blue-600" />
                          : <Square className="w-4 h-4 text-gray-300" />
                        }
                      </span>
                    )}
                    <span className={`${!alreadySigned ? "col-span-4" : "col-span-5"} text-gray-700`}>
                      {item.name}
                      {remise > 0 && item.discount_label && <span className="block text-xs text-red-600">{item.discount_label}</span>}
                    </span>
                    <span className="col-span-1 text-center text-gray-500">{item.quantity}</span>
                    <span className="col-span-2 text-right text-gray-500">{parseFloat(item.unit_price || 0).toFixed(2)}€</span>
                    <span className={`col-span-2 text-right ${remise > 0 ? "text-red-600 font-medium" : "text-gray-300"}`}>{remise > 0 ? `−${remise.toFixed(2)}€` : "—"}</span>
                    <span className="col-span-2 text-right font-medium text-gray-700">{lineTotal.toFixed(2)}€</span>
                  </div>
                );
              })}
            </div>

            {/* Totaux */}
            <div className="mt-3 space-y-1 text-sm">
              {!alreadySigned && selectedList.length < (quote.items || []).length && (
                <div className="flex justify-between text-gray-400 text-xs">
                  <span>Total devis complet</span><span>{total.toFixed(2)}€</span>
                </div>
              )}
              {remisesAffichees > 0 && (
                <div className="flex justify-between text-red-600 font-medium">
                  <span>Remises accordées</span><span>−{remisesAffichees.toFixed(2)}€</span>
                </div>
              )}
              <div className="flex justify-between text-gray-500">
                <span>Sous-total HT {!alreadySigned && selectedList.length < (quote.items||[]).length ? "(sélection)" : ""}</span>
                <span>{(!alreadySigned ? selSub : subtotal).toFixed(2)}€</span>
              </div>
              {parseFloat(quote.tva_rate || 0) > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>TVA ({quote.tva_rate}%)</span>
                  <span>{(!alreadySigned ? selTva : tvaAmt).toFixed(2)}€</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-base pt-2 border-t border-gray-200">
                <span className="text-gray-800">Total TTC</span>
                <span className="text-blue-600 text-lg">{(!alreadySigned ? selTotal : total).toFixed(2)}€</span>
              </div>
            </div>

            {quote.notes && (
              <div className="mt-4 p-3 bg-gray-50 rounded-lg text-sm text-gray-600">
                <p className="font-medium text-gray-700 mb-1">Notes</p>
                <p>{quote.notes}</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Zone signature */}
        {!alreadySigned && (
          <Card className="bg-white border-gray-200 shadow-sm">
            <CardContent className="p-5">
              {step === "view" && (
                <div className="text-center">
                  <PenTool className="w-10 h-10 text-blue-500 mx-auto mb-3" />
                  <h3 className="font-bold text-gray-800 mb-1">Accepter ce devis</h3>
                  <p className="text-sm text-gray-500 mb-2">
                    {selectedList.length} article{selectedList.length > 1 ? "s" : ""} sélectionné{selectedList.length > 1 ? "s" : ""} — Total : <strong className="text-blue-600">{selTotal.toFixed(2)}€</strong>
                  </p>
                  {selectedList.length === 0 ? (
                    <p className="text-sm text-red-500 mb-4">Veuillez sélectionner au moins un article</p>
                  ) : (
                    <p className="text-sm text-gray-500 mb-4">Renseignez vos informations et signez électroniquement.</p>
                  )}
                  <Button
                    onClick={() => setStep("sign")}
                    disabled={selectedList.length === 0}
                    className="bg-blue-600 hover:bg-blue-700 w-full disabled:opacity-50"
                  >
                    Procéder à la signature
                  </Button>
                </div>
              )}

              {step === "sign" && (
                <div className="space-y-4">
                  {/* Récap sélection */}
                  <div className="p-3 bg-blue-50 rounded-xl border border-blue-200">
                    <p className="text-xs font-semibold text-blue-700 mb-1">Articles acceptés ({selectedList.length})</p>
                    {selectedList.map((item, i) => (
                      <p key={i} className="text-xs text-blue-600">• {item.name} × {item.quantity} — {totalLigne(item).toFixed(2)}€{remiseU(item) > 0 && <span className="text-red-600"> (remise −{(remiseU(item) * parseFloat(item.quantity || 1)).toFixed(2)}€)</span>}</p>
                    ))}
                    <p className="text-sm font-bold text-blue-700 mt-2">Total : {selTotal.toFixed(2)}€</p>
                  </div>

                  <h3 className="font-bold text-gray-800">Vos informations</h3>
                  <div className="space-y-2">
                    <Input value={clientForm.client_name} onChange={e => setClientForm(f => ({...f, client_name: e.target.value}))} placeholder="Nom complet *" className="border-gray-300 bg-white text-gray-900" />
                    <div className="grid grid-cols-2 gap-2">
                      <Input value={clientForm.client_email} onChange={e => setClientForm(f => ({...f, client_email: e.target.value}))} placeholder="Email" type="email" className="border-gray-300 bg-white text-gray-900" />
                      <Input value={clientForm.client_phone} onChange={e => setClientForm(f => ({...f, client_phone: e.target.value}))} placeholder="Téléphone" className="border-gray-300 bg-white text-gray-900" />
                    </div>
                  </div>

                  <div>
                    <p className="text-sm font-medium text-gray-700 mb-2">Signature (bon pour accord)</p>
                    <div className="border-2 border-dashed border-gray-300 rounded-xl bg-gray-50 overflow-hidden">
                      <canvas ref={canvasRef} width={600} height={160} className="w-full touch-none cursor-crosshair" style={{ display: "block" }}
                        onMouseDown={startDraw} onMouseMove={draw} onMouseUp={stopDraw} onMouseLeave={stopDraw}
                        onTouchStart={startDraw} onTouchMove={draw} onTouchEnd={stopDraw} />
                    </div>
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-xs text-gray-400">Signez dans le cadre ci-dessus</p>
                      <button onClick={clearSig} className="text-xs text-red-400 hover:text-red-600 flex items-center gap-1">
                        <X className="w-3 h-3" /> Effacer
                      </button>
                    </div>
                  </div>

                  {/* CGV */}
                  {data?.shop?.cgv && (
                    <div className="bg-gray-50 rounded-xl p-3 max-h-40 overflow-y-auto border border-gray-200">
                      <p className="text-xs font-bold text-gray-600 mb-2">CONDITIONS GÉNÉRALES</p>
                      <pre className="text-xs text-gray-500 whitespace-pre-wrap font-sans leading-relaxed">{data.shop.cgv}</pre>
                    </div>
                  )}

                  <p className="text-xs text-gray-400 text-center">
                    En signant, vous acceptez les articles sélectionnés et les conditions générales.
                  </p>

                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setStep("view")} className="flex-1 border-gray-300 bg-white text-gray-800 hover:bg-gray-50 hover:text-gray-900">
                      Retour
                    </Button>
                    <Button onClick={handleSign} disabled={saving} className="flex-1 bg-blue-600 hover:bg-blue-700">
                      {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
                      Confirmer et signer
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* CGV publiques */}
        {data?.shop?.cgv && !alreadySigned && step === "view" && (
          <div className="bg-gray-100 rounded-xl p-4 text-xs text-gray-400 leading-relaxed">
            <p className="font-semibold text-gray-500 mb-2">CONDITIONS GÉNÉRALES</p>
            <pre className="whitespace-pre-wrap font-sans text-xs text-gray-400">{data.shop.cgv}</pre>
          </div>
        )}

        {/* Contact boutique */}
        <div className="text-center text-sm text-gray-400 pb-8">
          <p>{shop?.shop_name}</p>
          {shop?.shop_phone && <p>{shop.shop_phone}</p>}
          {shop?.shop_email && <p>{shop.shop_email}</p>}
        </div>
      </div>
    </div>
  );
}
